/* eslint-disable consistent-return, default-case, no-param-reassign */
/*
 *
 * Flex Draft reducer
 *
 */

import produce from 'immer';

import { RESET_SWITCH_LOAN_STATE, RESET_USER_STATE } from '../../Main/constants';
import {
  CHANGE_INPUT,
  CHANGE_STEP,
  CHANGE_VALIDATION_FAILURE,
  FLEX_DRAFT_CHANGE,
  FLEX_DRAFT_CHANGE_FAILURE,
  FLEX_DRAFT_CHANGE_SUCCESS,
  FLEX_DRAFT_FETCH_STATUS,
  FLEX_DRAFT_FETCH_STATUS_FAILURE,
  FLEX_DRAFT_FETCH_STATUS_SUCCESS,
  RESET_CONFIRMATION,
  RESET_FLEX_DRAFT_STATE,
  RESET_FORM_STATE,
  RESET_REQUEST_STATUS,
  SET_IS_AMOUNT_DRAWER_OPEN,
  SET_IS_IN_FLEX_DRAFT_FLOW,
  SET_IS_MODAL_OPEN,
  SET_IS_SCHEDULE_DRAWER_OPEN,
  SET_IS_SECOND_ACCOUNT_FORM_ENABLED,
  SET_SELECTED_DRAFT_SCHEDULE_STEP,
  UPDATE_AMOUNT,
  UPDATE_FORM_ERROR,
} from './constants';

export const initialState = {
  confirmationNumber: null,
  errors: {
    serviceError: {
      flexDraftChange: false,
      flexDraftFetchStatus: false,
    },
  },
  formErrors: { amount: { additionalPrincipal: '' } },
  forms: {
    amount: { additionalPrincipal: null },
    scheduleBiWeekly: {
      startingDay: '',
      startingMonth: '',
    },
    scheduleSelect: { draftSchedule: 'scheduleSemiMonthly' },
    scheduleSemiMonthly: {
      firstDraft: '',
      secondDraft: '',
    },
  },
  isAmountDrawerOpen: false,
  isInFlexDraftFlow: false,
  isModalOpen: { cancel: false },
  isScheduleDrawerOpen: false,
  isSecondAccountFormEnabled: false,
  requestStatus: {
    flexDraftChange: 'idle',
    flexDraftFetchStatus: 'idle',
  },
  selectedDraftScheduleStep: '',
  statusData: {
    allowedOperation: '',
    allowedSetting: {},
    pendingSetting: {},
    statement: {},
    status: '',
  },
  step: 1,
};

const flexDraftReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case CHANGE_INPUT: {
      const { field, form, value } = payload;
      draft.forms[form][field] = value;
      if (draft.formErrors[form] && draft.formErrors[form][field]) {
        draft.formErrors[form][field] = '';
      }
      break;
    }
    case CHANGE_STEP: {
      const { step } = payload;
      draft.step = step;
      break;
    }
    case CHANGE_VALIDATION_FAILURE: {
      /**
       * Pattern matching Autopay. Currently only amount errors are possible; in future,
       * possibly account or other errors as well.
       */
      const { amount: amountFormErrors } = payload;
      Object.keys(amountFormErrors).forEach((key) => {
        draft.formErrors.amount[key] = amountFormErrors[key];
      });
      const hasAmountErrors = Object.values(draft.formErrors.amount).some((value) => !!value);
      if (hasAmountErrors) {
        draft.isAmountDrawerOpen = true;
      }
      draft.requestStatus.flexDraftChange = 'failed';
      break;
    }
    case FLEX_DRAFT_CHANGE: {
      draft.requestStatus.flexDraftChange = 'loading';
      break;
    }
    case FLEX_DRAFT_CHANGE_FAILURE: {
      draft.errors.serviceError.flexDraftChange = true;
      draft.isInFlexDraftFlow = initialState.isInFlexDraftFlow;
      draft.requestStatus.flexDraftChange = 'failed';
      break;
    }
    case FLEX_DRAFT_CHANGE_SUCCESS: {
      const { confirmationNumber } = payload;
      draft.confirmationNumber = confirmationNumber;
      draft.isInFlexDraftFlow = initialState.isInFlexDraftFlow;
      draft.requestStatus.flexDraftChange = 'succeeded';
      break;
    }
    case FLEX_DRAFT_FETCH_STATUS: {
      draft.errors.serviceError.flexDraftFetchStatus =
        initialState.errors.serviceError.flexDraftFetchStatus;
      draft.requestStatus.flexDraftFetchStatus = 'loading';
      break;
    }
    case FLEX_DRAFT_FETCH_STATUS_FAILURE: {
      const { error } = payload;
      draft.errors.serviceError.flexDraftFetchStatus = error;
      draft.requestStatus.flexDraftFetchStatus = 'failed';
      draft.statusData = initialState.statusData;
      break;
    }
    case FLEX_DRAFT_FETCH_STATUS_SUCCESS: {
      const {
        allowedOperation,
        allowedSetting,
        pendingSetting,
        statement,
        status,
      } = payload;
      draft.requestStatus.flexDraftFetchStatus = 'succeeded';
      draft.statusData.allowedOperation = allowedOperation;
      draft.statusData.allowedSetting = allowedSetting;
      draft.statusData.pendingSetting = pendingSetting;
      draft.statusData.statement = statement;
      draft.statusData.status = status;
      break;
    }
    case RESET_CONFIRMATION: {
      draft.confirmationNumber = initialState.confirmationNumber;
      draft.requestStatus.flexDraftChange = initialState.requestStatus.flexDraftChange;
      break;
    }
    case RESET_FLEX_DRAFT_STATE: {
      draft.formErrors = initialState.formErrors;
      draft.forms = initialState.forms;
      draft.isInFlexDraftFlow = initialState.isInFlexDraftFlow;
      draft.isScheduleDrawerOpen = initialState.isScheduleDrawerOpen;
      draft.isSecondAccountFormEnabled = initialState.isSecondAccountFormEnabled;
      draft.requestStatus = initialState.requestStatus;
      draft.selectedDraftScheduleStep = initialState.selectedDraftScheduleStep;
      draft.step = initialState.step;
      break;
    }
    case RESET_FORM_STATE: {
      const { form } = payload;
      draft.forms[form] = initialState.forms[form];
      if (draft.formErrors[form]) {
        draft.formErrors[form] = initialState.formErrors[form];
      }
      break;
    }
    case RESET_REQUEST_STATUS: {
      const { statusType } = payload;
      draft.requestStatus[statusType] = 'idle';
      break;
    }
    case RESET_SWITCH_LOAN_STATE: {
      return initialState;
    }
    case RESET_USER_STATE: {
      return initialState;
    }
    case SET_IS_AMOUNT_DRAWER_OPEN: {
      const { value } = payload;
      draft.isAmountDrawerOpen = value;
      break;
    }
    case SET_IS_IN_FLEX_DRAFT_FLOW: {
      const { value } = payload;
      draft.isInFlexDraftFlow = value;
      break;
    }
    case SET_IS_MODAL_OPEN: {
      const { key, value } = payload;
      draft.isModalOpen[key] = value;
      break;
    }
    case SET_IS_SCHEDULE_DRAWER_OPEN: {
      const { value } = payload;
      draft.isScheduleDrawerOpen = value;
      break;
    }
    case SET_IS_SECOND_ACCOUNT_FORM_ENABLED: {
      const { value } = payload;
      draft.isSecondAccountFormEnabled = value;
      break;
    }
    case SET_SELECTED_DRAFT_SCHEDULE_STEP: {
      const { step } = payload;
      draft.selectedDraftScheduleStep = step;
      break;
    }
    case UPDATE_AMOUNT: {
      const { amount } = payload;
      draft.forms.amount = amount;
      if (draft.formErrors.amount.additionalPrincipal) {
        draft.formErrors.amount.additionalPrincipal =
          initialState.formErrors.amount.additionalPrincipal;
      }
      break;
    }
    case UPDATE_FORM_ERROR: {
      const { field, form, value } = payload;
      draft.formErrors[form][field] = value;
      break;
    }
  }
}, initialState);

export default flexDraftReducer;
